import {
  Component,
  ElementRef,
  Input,
  SimpleChanges,
  ViewChild,
  OnChanges,
} from '@angular/core';
import { ZiraService, environment } from '@dal/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
@Component({
  selector: 'dal-shared-ui-media-renderer',
  templateUrl: './media-renderer.component.html',
  styleUrl: './media-renderer.component.scss',
})
export class MediaRendererComponent implements OnChanges {
  @Input() url!: string;
  @Input() type!: string;
  @Input() imagePreview!: boolean;
  @Input() applyClass: boolean = true;
  @Input() authenticateUrl: boolean = true;
  @Input() customClass: string = '';

  @ViewChild('audioPlayer', { static: false })
  audioPlayer!: ElementRef<HTMLAudioElement>;
  @ViewChild('videoPlayer', { static: false })
  videoPlayer!: ElementRef<HTMLVideoElement>;

  renditionId: string;
  originalId: string;
  inputUrl: string | null = null; // raw string
  safeUrl: SafeResourceUrl | null = null; // sanitized value
  idToFetch: string;

  // Update trusted domains based on workspace context
  private trustedDomains: string[] = [new URL(environment.apiBaseUrl).hostname, 'localhost'];

  constructor(
    private ziraService: ZiraService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['url'] && this.url) {
      if (this.authenticateUrl) {
        this.extractIdsFromUrl(this.url);
        this.idToFetch = this.renditionId || this.originalId;
        if (this.idToFetch) {
          this.ziraService.getAssetUrl(this.idToFetch).subscribe((blob) => {
            const objectUrl = URL.createObjectURL(blob);
            this.inputUrl = objectUrl;
            this.safeUrl = this.sanitizeAndValidateUrl(objectUrl);
          });
        }
      } else {
        this.inputUrl = this.url;
        this.safeUrl = this.sanitizeAndValidateUrl(this.url);
      }
    }
  }

  private extractIdsFromUrl(url: string): void {
    const parts = url.split('/');
    if (parts.length >= 2) {
      this.renditionId = parts[parts.length - 1];
      this.originalId = parts[parts.length - 2];
    } else if (parts.length === 1) {
      this.originalId = parts[0];
    }
  }


  private sanitizeAndValidateUrl(url: string | null): SafeResourceUrl | null {
    if (!url) return null;
    try {
      const parsedUrl = new URL(url, window.location.origin);
      // Allow blob URLs directly
      if (parsedUrl.protocol === 'blob:') {
        return url;
      }
      // Allow local static asset URLs
      if (parsedUrl.pathname.startsWith('/assets/')) {
        return url;
      }
      // Only allow http(s) URLs from trusted domains
      if (['http:', 'https:'].includes(parsedUrl.protocol) && this.trustedDomains.includes(parsedUrl.hostname)) {
        return url;
      }
    } catch (e) {
      console.error('Invalid URL:', url);
    }
    // Log or handle invalid or untrusted URLs
    console.warn('Untrusted or invalid URL:', url);
    return null;
  }
  
  get imageUrl(): string {
    if (!this.inputUrl) return '';
    // Allow blob URLs directly
    if (this.inputUrl.startsWith('blob:')) {
      return this.inputUrl;
    }
    // Allow local static asset URLs
    if (this.inputUrl.startsWith('/assets/')) {
      return this.inputUrl;
    }
    // Only allow http(s) URLs from trusted domains
    const urlPattern = /^https?:\/\//;
    if (urlPattern.test(this.inputUrl)) {
      try {
        const parsedUrl = new URL(this.inputUrl);
        if (this.trustedDomains.includes(parsedUrl.hostname)) {
          return this.inputUrl;
        }
      } catch (e) {
        console.error('Invalid image URL:', this.inputUrl);
      }
    }
    console.warn('Untrusted or invalid image URL:', this.inputUrl);
    return '';
  }
}
